import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import styled from 'styled-components';
import { Alert, Button, Checkbox, CheckListItem, Empty, FieldWrapper, Filter, FilterButton, Input, Label, LayoutSidebar, SectionHeader, Select, TipCalcChecksSidebar, Tooltip } from 'components';
import { MakePaymentNotification } from 'config/const';
import { lineHeight, theme, toRem } from 'design';
import { useDebounce } from 'hooks';
import { selectCalculatorChangeLog, selectCalculatorPaymentSet, selectCalculatorPOSResults, setPOSResults } from 'store/TipCalculator';
import { ChangeLogAction, ChangeLogEntity, ICalculatorRules, ICheck, IPOSTicket, IPOSTicketSplitOverride, ISite, ITipCalculatorChangeLogEntry, TicketOverrideType } from 'types';
import { forceRender, sortAlphaNumerically } from 'utils';
import { applyCashTipEdit, applyTicketOverride, buildCheckChangeLogEntries, buildSortedChecksList } from 'utils/tipCalculator';
import InfiniteScroll from '../../../../components/InfiniteScroll/InfiniteScroll';
import { MPContent } from '../../styles';
import { EditCheckAmounts } from '../components/modals/EditCheckAmountsModal/EditCheckAmountsModal';
import { IgnoreChecksModal } from '../components/modals/IgnoreChecksModal/IgnoreChecksModal';
import { SplitCheckModal } from '../components/modals/SplitCheckModal/SplitCheckModal';
import { TipCalculatorProcessingModal } from '../components/modals/TipCalculatorProcessingModal';
import { TipCalculatorProcessingModalMode } from '../components/modals/TipCalculatorProcessingModal/types';
import { UndoCheckChangesModal } from '../components/modals/UndoCheckChangesModal/UndoCheckChangesModal';

interface IChecksProps {
  currentCalculatorRules: ICalculatorRules;
  site: ISite;
  pageHeaderContent: React.ReactNode;
  onBack: () => void;
}

interface IFilters {
  checkNumber: string | undefined;
  employeeName: string | undefined;
  checkTypes: string[] | undefined;
}

const StickyHeader = styled.div`
  display: flex;
  flex-direction: column;
  position: sticky;
  background-color: ${theme.color.white};
  top: ${toRem(66)};
  z-index: ${theme.zIndex.sidebar - 1};
  width: 100%;
  background: linear-gradient(to bottom, ${theme.color.white} 90%, transparent 100%);
  padding: ${toRem(theme.space.lg)} ${toRem(theme.space.xl)} 0;

  @media (min-width: ${theme.breakpoint.small}px) {
    padding: ${toRem(theme.space.lg)} ${toRem(theme.space.xl)} 0 ${toRem(theme.space['3xl'])};
  }
`;

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-right: ${toRem(theme.space.lg)};
  gap: ${toRem(theme.space.md)};
  div {
    gap: ${toRem(theme.space.md)};
  }

  @media (max-width: ${theme.breakpoint.small}px) {
    flex-wrap: wrap-reverse;
  }
`;

const SelectedItemControl = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${toRem(theme.space.xs)};
  /* off-scale exception: sub-pixel optical nudge, same class as the 1px exceptions (2026-09-16) */
  margin-top: ${toRem(0.5)};

  label {
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
    font-weight: ${theme.font.weight.medium};
    color: ${theme.color.neutral[500]};
  }
`;

const ClearSelected = styled.button`
  /* off-scale exception: no font-size token below xxs (11px) */
  font-size: ${toRem(10)};
  font-weight: 500;
  color: ${theme.color.primary[600]};
  /* off-scale exception: 1px optical nudge aligning the 10px link with its label (2026-09-16) */
  margin-top: ${toRem(1)};

  &:hover {
    cursor: pointer;
    color: ${theme.color.primary[800]};
  }
`;

const ITEM_LIST_OFFSET = 10;

type CheckAction = ChangeLogAction.SPLIT | ChangeLogAction.IGNORE | ChangeLogAction.UNDO_OVERRIDE;

// The override each action writes to a check. Undo writes nothing, which removes the existing one.
const overrideForAction = (action: CheckAction, index: number, splitOverrides?: IPOSTicketSplitOverride[]): IPOSTicket['override'] => {
  if (action === ChangeLogAction.SPLIT) return splitOverrides[index];
  if (action === ChangeLogAction.IGNORE) return { type: TicketOverrideType.IGNORE };
  return undefined;
};

// Which bulk actions the current selection allows
const getDisabledActions = (selectedChecks: ICheck[]) => ({
  disableSplit: !selectedChecks.some((check) => check.totals.gross > 0),
  disableIgnore: selectedChecks.length === 0,
  disableUndo: !selectedChecks.some((check) => !!check.override),
});

// The sidebar's ignored and split sections plus the ignored total, in one pass
const summarizeOverriddenChecks = (checks: ICheck[] = []) =>
  checks.reduce<{ ignoredChecks: ICheck[]; splitChecks: ICheck[]; ignoredTotal: number }>(
    (acc, check) => {
      if (check.override?.type === TicketOverrideType.IGNORE) {
        acc.ignoredChecks.push(check);
        acc.ignoredTotal += check.totals.gross;
      } else if (check.override?.type === TicketOverrideType.SPLIT) {
        acc.splitChecks.push(check);
      }
      return acc;
    },
    { ignoredChecks: [], splitChecks: [], ignoredTotal: 0 }
  );

const filterChecks = (checks: ICheck[], filters?: IFilters): ICheck[] => {
  const checkNumber = filters?.checkNumber?.trim().toLowerCase();
  const employeeName = filters?.employeeName?.trim().toLowerCase();
  const checkTypes = filters?.checkTypes;

  if (!checkNumber && !employeeName && !checkTypes) return checks;

  return checks.filter((check) => {
    if (checkNumber && !check.displayId.toLowerCase().startsWith(checkNumber)) return false;
    if (employeeName && !check.employeeName.toLowerCase().includes(employeeName)) return false;
    if (checkTypes && !checkTypes.includes(check.override?.type)) return false;
    return true;
  });
};

const Checks = ({ site, currentCalculatorRules, onBack, pageHeaderContent }: IChecksProps) => {
  const dispatch = useDispatch();
  const [filterActive, setFilterActive] = useState<boolean>(false);
  const [filters, setFilters] = useState<IFilters>();
  const [editCheck, setEditCheck] = useState<ICheck>();
  const [maxOffset, setMaxOffset] = useState<number>(ITEM_LIST_OFFSET);
  const [isLoadingBulkAction, setIsLoadingBulkAction] = useState<boolean>(false);
  const [checkList, setCheckList] = useState<ICheck[]>();
  const { timezone: currentSiteTimezone, tipCalculatorPercentFee } = site?.settings || {};
  const paymentSet = useSelector(selectCalculatorPaymentSet());
  const paymentSetSegments = paymentSet?.segments;
  const paymentDate = paymentSet?.date;
  const posDataResults = useSelector(selectCalculatorPOSResults());
  const [posDataClone, setPOSDataClone] = useState(() => structuredClone(posDataResults));

  // Change log entries
  const changeLogEntries = useSelector(selectCalculatorChangeLog());
  const [currentChangeLogEntries, setCurrentChangeLogEntries] = useState<ITipCalculatorChangeLogEntry[]>(changeLogEntries);

  const dirty = useMemo(() => !isEqual(posDataResults, posDataClone), [posDataResults, posDataClone]);

  // All derived from `checkList`, which also holds the selected flags.
  const filteredCheckList = useMemo(() => (checkList ? filterChecks(checkList, filters) : undefined), [checkList, filters]);
  const selectedChecks = useMemo(() => (checkList || []).filter((check) => check.selected), [checkList]);
  const selectAllChecked = filteredCheckList?.length > 0 && filteredCheckList.every((check) => check.selected);
  const isLoading = !checkList;
  const totalTipsReceived = useMemo(() => checkList?.reduce((acc, check) => acc + check.totals.gross, 0), [checkList]);

  // Show "X of Y" only while a filter is narrowing the list
  const isFiltered = filters && filteredCheckList && checkList && filteredCheckList.length !== checkList.length;
  const checksCountText = isFiltered ? `${filteredCheckList.length} of ${checkList.length}` : checkList?.length.toString() || '0';
  const { ignoredChecks, splitChecks, ignoredTotal } = useMemo(() => summarizeOverriddenChecks(checkList), [checkList]);
  const appliedFilters = Object.values(filters || {}).filter((value) => value);
  const { disableSplit, disableIgnore, disableUndo } = getDisabledActions(selectedChecks);

  // Rebuild the check list whenever the POS data changes; this also clears any selection
  useEffect(() => setCheckList(buildSortedChecksList(posDataClone)), [posDataClone, paymentDate]);

  const updateFilters = (update: Partial<IFilters>) => {
    setFilters((current) => ({ ...current, ...update }));
    setMaxOffset(ITEM_LIST_OFFSET); // reset the maxOffset when changing filters
  };

  const onFilterCheckByNumber = useDebounce((value: string) => {
    const checkNumber = value.charAt(0) === '#' ? value.slice(1) : value; // remove # if it is the first character in the string.
    updateFilters({ checkNumber: !isEmpty(checkNumber) ? checkNumber : undefined });
  });

  const onFilterEmployeeName = useDebounce((value: string) => updateFilters({ employeeName: !isEmpty(value) ? value : undefined }));
  const onFilterCheckType = useDebounce((value: { value: string; label: string }[]) => updateFilters({ checkTypes: !isEmpty(value) ? value.map((item) => item.value) : undefined }));

  /**
   * Runs an action over a batch of checks against a fresh clone of the POS data, then commits the
   * edits, the change log and the modal close in a single state update.
   * `splitOverrides` applies to SPLIT only, matched to `checks` by index.
   */
  const applyCheckAction = async (action: CheckAction, checks: ICheck[], hideModal: () => void, splitOverrides?: IPOSTicketSplitOverride[]) => {
    setIsLoadingBulkAction(true);
    await forceRender();

    const posData = structuredClone(posDataClone);
    let updatedChangeLogEntries = currentChangeLogEntries;

    // Process all checks in a single batch
    checks.forEach((check, index) => {
      if (applyTicketOverride(posData, check, overrideForAction(action, index, splitOverrides))) {
        updatedChangeLogEntries = buildCheckChangeLogEntries(action, updatedChangeLogEntries, posDataClone, check);
      }
    });

    // Make a single state update with all changes. Rebuilding the check list clears the selection.
    setCurrentChangeLogEntries(updatedChangeLogEntries);
    setPOSDataClone(posData);

    // Hide modal after all checks are processed
    setIsLoadingBulkAction(false);
    hideModal();
  };

  const handleSplit = (checks: ICheck[], overrides: IPOSTicketSplitOverride[]) => applyCheckAction(ChangeLogAction.SPLIT, checks, hideSplitCheckModal, overrides);
  const handleIgnoreCheck = (checks: ICheck[]) => applyCheckAction(ChangeLogAction.IGNORE, checks, hideIgnoreCheckModal);
  const onUndoCheckOverride = (checks: ICheck[]) => applyCheckAction(ChangeLogAction.UNDO_OVERRIDE, checks, hideUndoChangesWarningModal);

  const handleEditCheckAmounts = useCallback(
    (check: ICheck) => {
      const posData = structuredClone(posDataClone);

      if (applyCashTipEdit(posData, check)) {
        setCurrentChangeLogEntries(buildCheckChangeLogEntries(ChangeLogAction.CASH_EDITED, currentChangeLogEntries, posDataClone, check));
        setPOSDataClone(posData);
      }

      hideEditCheckAmountsModal();
    },
    [posDataClone]
  );

  const onEditAmounts = (check: ICheck) => {
    setEditCheck(check);
    showEditCheckAmountsModal();
  };

  const setChecksSelected = (checked: boolean, isMatch: (check: ICheck) => boolean) => setCheckList(checkList.map((check) => (isMatch(check) ? { ...check, selected: checked } : check)));

  const onSelectCheck = (selectedCheck: ICheck, checked: boolean) => setChecksSelected(checked, (check) => check.key === selectedCheck.key);

  const onToggleSelectAllChecks = (checked: boolean, clearAll = false) => {
    // Select all only applies to the checks in view, so match against the filtered keys
    const filteredKeys = new Set(filteredCheckList.map((check) => check.key));
    setChecksSelected(checked, (check) => clearAll || filteredKeys.has(check.key));
  };

  const loadMoreChecks = useCallback(async () => {
    if (filteredCheckList.length > maxOffset) {
      setMaxOffset(maxOffset + ITEM_LIST_OFFSET);
    }
  }, [filteredCheckList, maxOffset]);

  const [showAbandonChangesModal, hideAbandonChangesModal] = useModal(
    () => (
      <Alert
        dataCy="abandon-changes-alert"
        title="Abandon Changes?"
        message="You have unsaved changes that haven't been applied to your calculations. Are you sure you want to discard edits?"
        onLeftAction={hideAbandonChangesModal}
        onRightAction={onBack}
        leftActionLabel="Cancel"
        rightActionLabel="Abandon Changes"
        rightActionAppearance="destructive"
        isActive
      />
    ),
    [onBack]
  );

  const [showIgnoreCheckModal, hideIgnoreCheckModal] = useModal(
    () => <IgnoreChecksModal checks={selectedChecks} onClose={hideIgnoreCheckModal} onApply={() => handleIgnoreCheck(selectedChecks)} isLoading={isLoadingBulkAction} />,
    [selectedChecks, isLoadingBulkAction, handleIgnoreCheck]
  );

  const [showUndoChangesWarningModal, hideUndoChangesWarningModal] = useModal(
    () => <UndoCheckChangesModal checks={selectedChecks} onClose={hideUndoChangesWarningModal} onApply={() => onUndoCheckOverride(selectedChecks)} isLoading={isLoadingBulkAction} />,
    [selectedChecks, isLoadingBulkAction, onUndoCheckOverride]
  );

  const [showSplitCheckModal, hideSplitCheckModal] = useModal(() => <SplitCheckModal checks={selectedChecks} onClose={hideSplitCheckModal} onSave={handleSplit} />, [selectedChecks, handleSplit]);

  const [showEditCheckAmountsModal, hideEditCheckAmountsModal] = useModal(
    () => <EditCheckAmounts check={editCheck} onClose={hideEditCheckAmountsModal} timeZone={currentSiteTimezone} onSave={handleEditCheckAmounts} />,
    [editCheck, currentSiteTimezone, handleEditCheckAmounts]
  );

  const [showIntegrationDataProcessingModal, hideIntegrationDataProcessingModal] = useModal(
    () => (
      <TipCalculatorProcessingModal
        mode={TipCalculatorProcessingModalMode.RECALCULATE}
        tipCalcRules={currentCalculatorRules}
        onClose={hideIntegrationDataProcessingModal}
        businessDate={paymentDate}
        site={site}
        segments={paymentSetSegments}
        posDataResults={posDataClone}
        changeLogEntries={currentChangeLogEntries}
        onSuccess={() => dispatch(setPOSResults(posDataClone))}
      />
    ),
    [currentCalculatorRules, paymentDate, site, paymentSet, posDataClone]
  );

  const selectedItemControl = () => {
    if (selectedChecks.length) {
      return (
        <SelectedItemControl>
          <Label>{`${selectedChecks.length} Check${selectedChecks.length === 1 ? '' : 's'} Selected`}</Label>
          <ClearSelected onClick={() => onToggleSelectAllChecks(false, true)}>Clear Selected</ClearSelected>
        </SelectedItemControl>
      );
    }
    return null;
  };

  const sidebarNotifications = () => {
    // If the total tips received are less than or equal to 0, return the no tips to be paid notification
    if (totalTipsReceived <= 0) return MakePaymentNotification.tipCalculator.noTipsToBePaid;

    // If the ignored total are greater than or equal to the total tips received, return the all checks ignored notification
    if (ignoredTotal >= totalTipsReceived) return MakePaymentNotification.tipCalculator.allChecksIgnored;

    return undefined;
  };

  return (
    <LayoutSidebar
      content={
        <>
          {pageHeaderContent}
          <StickyHeader>
            <SectionHeader data-cy="checks-header" title={`Checks (${checksCountText})`} subtitle={selectedItemControl()} isLoading={isLoading}>
              <Controls data-cy="checks-header-controls">
                <FilterButton isLoading={isLoading} filterActive={filterActive} appliedFilters={appliedFilters.length} onClick={() => setFilterActive(!filterActive)} />
                <div>
                  <Button data-cy="split-button" isLoading={isLoading} appearance="white" label="Split" onClick={showSplitCheckModal} size="small" icon="Split" disabled={disableSplit} />
                  <Button data-cy="ignore-button" isLoading={isLoading} appearance="white" label="Ignore" onClick={showIgnoreCheckModal} size="small" icon="No" disabled={disableIgnore} />
                  <Button data-cy="undo-button" isLoading={isLoading} label="Undo" onClick={showUndoChangesWarningModal} size="small" icon="Retry" appearance="white" disabled={disableUndo} />
                  <Tooltip id="select-all-checks-tooltip" place="top">
                    Select all
                  </Tooltip>
                  <Checkbox
                    id="checks-select-all"
                    data-cy="checks-select-all"
                    size="large"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onToggleSelectAllChecks(e.target.checked)}
                    checked={selectAllChecked}
                    data-tooltip-id="select-all-checks-tooltip"
                  />
                </div>
              </Controls>
            </SectionHeader>
            <Filter isVisible={filterActive} data-cy="filter-section">
              <FieldWrapper label="Check Number">
                <Input id="checkNumber" type="text" data-cy="check-number-input" placeholder="Search..." maxLength={20} autoComplete="off" onChange={(e) => onFilterCheckByNumber(e.target.value)} />
              </FieldWrapper>
              <FieldWrapper label="Employee Name">
                <Input id="employeeName" type="text" data-cy="employee-name-input" placeholder="Search..." maxLength={50} autoComplete="off" onChange={(e) => onFilterEmployeeName(e.target.value)} />
              </FieldWrapper>
              <FieldWrapper label="Check Type">
                <Select
                  options={[
                    { label: 'Split', value: TicketOverrideType.SPLIT },
                    { label: 'Ignored', value: TicketOverrideType.IGNORE },
                  ]}
                  values={[]}
                  multi
                  onChange={(selected) => onFilterCheckType(selected)}
                  data-cy="check-type-select"
                />
              </FieldWrapper>
            </Filter>
          </StickyHeader>
          <MPContent data-cy="checks-list" $smallGutter>
            {!isLoading && filteredCheckList && (
              <>
                {!filteredCheckList.length ? (
                  <Empty container containerHeight={250} icon="Ticket" message="No checks found" />
                ) : (
                  <InfiniteScroll fetchMore={loadMoreChecks}>
                    {filteredCheckList.slice(0, maxOffset).map((check) => (
                      <CheckListItem
                        check={check}
                        key={`check-list-item-${check.key}`}
                        timeZone={currentSiteTimezone}
                        onEditAmounts={onEditAmounts}
                        categoryGroups={currentCalculatorRules.categoryGroups || []}
                        creditCardFee={tipCalculatorPercentFee}
                        onSelect={onSelectCheck}
                      />
                    ))}
                  </InfiniteScroll>
                )}
              </>
            )}
          </MPContent>
        </>
      }
      sidebar={
        <TipCalcChecksSidebar
          businessDate={dayjs(paymentDate)}
          total={totalTipsReceived}
          ignoredChecks={ignoredChecks}
          splitChecks={splitChecks}
          cashEditChangeLogEntries={currentChangeLogEntries.filter((item) => item.entity === ChangeLogEntity.CHECK && item.action === ChangeLogAction.CASH_EDITED).sort(sortAlphaNumerically('details.displayId'))}
          onBack={dirty ? showAbandonChangesModal : onBack}
          onContinue={() => showIntegrationDataProcessingModal()}
          continueDisabled={!posDataClone.data.length || !dirty || totalTipsReceived <= 0}
          notifications={[sidebarNotifications()].filter(Boolean)}
        />
      }
    />
  );
};

export default Checks;
